import React, { FC, SVGProps, useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import BaseButton from "../Common/Buttons/BaseButton";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import { uploadMultipleFilesAction } from "../Projects/Store/projects.action";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  addRagQuestionAction,
  deleteRagAction,
  getAllRagQuestionsAction,
  updateRagAction,
  uploadRagAction,
} from "../ProjectSection/Agents/Store/Agent.action";
import { ReactComponent as DeleteIcon } from "../../../assets/Images/Delete.svg";
import { ReactComponent as EditIcon } from "../../../assets/Images/EditIcon.svg";

import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { shortenString } from "../../../Constant/helper";
import DeletePopup from "../CommonComponents/PopUpCards/DeletePopUp";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import PageLoader from "../CommonComponents/PageLoader";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddIcon from "@mui/icons-material/Add";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { swalWarningMessage } from "../../../Constant/swalMessage";
import SearchBar from "../Common/Search/SearchBar";
import useDebounce from "../../Hooks/useDebounce";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import { PAGE_SIZE_50 } from "../CommonComponents/Common";
import RagEditBody from "./RagEditBody";
import AddRagBody from "./AddRagBody";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import {
  getDecryptedItem,
  setEncryptedItem,
} from "../../../utils/localStorageHelper";

const AnimatedHourglass = () => (
  <HourglassBottomIcon
    sx={{
      fontSize: 85,
      color: "#228B22",
      animation: "spin 2s linear infinite",
      "@keyframes spin": {
        "0%": { transform: "rotate(0deg)" },
        "100%": { transform: "rotate(360deg)" },
      },
    }}
  />
);

type PopupState = {
  open: boolean;
  message: string;
  heading: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
};

const RagUpload = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    ragData: [],
    showUploadModal: false,
    showEditModal: false,
    confirmationPopUp: false,
    ragId: "",
    question: "",
    answer: "",
    searchText: "",
    addModal: false,
  });
  const [files, setFiles] = useState<File[]>([]);
  const { projectId } = useParams<{ projectId: string }>();
  const [mainLoading, setMainLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRagCount, setTotalRagCount] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [reuploadConfirmationPopup, setReuploadConfirmationPopup] =
    useState(false);
  const [popupState, setPopupState] = useState<PopupState>({
    open: false,
    message: "",
    heading: "",
    Icon: SuccessIcon,
  });
  const { open, message, heading, Icon } = popupState;

  const {
    ragData,
    showUploadModal,
    showEditModal,
    confirmationPopUp,
    ragId,
    question,
    answer,
    searchText,
    addModal,
  } = state;

  const debouncedSearchTerm = useDebounce(searchText, 3000);

  const fetchRagData = async () => {
    let totalCount = 0;
    const params = {
      project_id: projectId,
      page: currentPage,
      page_limit: PAGE_SIZE_50,
    };
    if (searchText) {
      params["search"] = searchText;
    }
    const res: any = await dispatch(getAllRagQuestionsAction(params));
    if (res?.data?.length > 0) {
      if (res?.totalCount && res?.totalCount?.length > 0) {
        totalCount = res?.totalCount[0]?.count || 0;
      } else {
        totalCount = res?.data?.length || 0;
      }
    }

    setState((prev) => ({
      ...prev,
      ragData: res?.data,
    }));
    setTotalRagCount(totalCount);
  };

  useEffect(() => {
    const init = async () => {
      setMainLoading(true);
      await fetchRagData();
      setMainLoading(false);
    };
    init();
  }, [dispatch, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    if (totalRagCount) {
      setTotalPages(Math.ceil(totalRagCount / PAGE_SIZE_50));
    }
  }, [currentPage, searchText, totalRagCount]);

  const handleUpload = async (files: File[]) => {
    setLoading(true);
    if (!files || files.length === 0) {
      swalWarningMessage("Please add at least one file");
      setLoading(false);
      return;
    }
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("file", file);
    });
    formData.append("voiceName", "vodex");
    formData.append("consentName", "vodex");
    formData.append("TNC", "true");

    const res: any = await dispatch(uploadMultipleFilesAction(formData));
    const fileArray = res?.map((item, idx) => {
      const file = files[idx];
      const extension =
        file?.name?.split(".")?.pop()?.toLowerCase() || "unknown";

      return {
        file_url: item?.url,
        file_type: extension,
      };
    });
    const obj = {
      files: fileArray,
      project_id: projectId,
    };
    await dispatch(uploadRagAction(obj));
    await fetchRagData();
    setState((prev) => ({
      ...prev,
      showUploadModal: false,
    }));
    setPopupState({
      open: true,
      message:
        "We're generating your questions. This may take up to 10 minutes. Please check back shortly!",
      heading: "In Progress",
      Icon: AnimatedHourglass,
    });
    setFiles([]);
    setLoading(false);
    setEncryptedItem("hasUploadedRagBefore", true);
  };

  const onUploadClick = (confirmed = false) => {
    const hasUploadedBefore = getDecryptedItem("hasUploadedRagBefore");

    if (hasUploadedBefore && !confirmed) {
      setReuploadConfirmationPopup(true);
      return;
    }

    handleUpload(files);
    setReuploadConfirmationPopup(false);
  };
  const editToggle = (selectedData, edit = false) => {
    setIsEdit(edit);

    setState((prev) => ({
      ...prev,
      showEditModal: true,
      question: selectedData?.question,
      answer: selectedData?.answer,
      ragId: selectedData?._id,
    }));
  };

  const handleDelete = async () => {
    setLoading(true);

    await dispatch(deleteRagAction(ragId));
    await fetchRagData();
    setState((prev) => ({
      ...prev,
      confirmationPopUp: false,
      ragId: "",
    }));
    setPopupState({
      open: true,
      message: "Question deleted successfully.",
      heading: "Question Deleted",
      Icon: SuccessIcon,
    });
    setFiles([]);
    setLoading(false);
  };

  const updateRagData = async () => {
    setLoading(true);
    const obj = {
      question: question,
      answer: answer,
    };

    await dispatch(updateRagAction(ragId, obj));
    await fetchRagData();
    setState((prev) => ({
      ...prev,
      showEditModal: false,
      ragId: "",
    }));
    setPopupState({
      open: true,
      message: "Question updated successfully.",
      heading: "Question Updated",
      Icon: SuccessIcon,
    });
    setLoading(false);
  };

  const addQuestionAns = async () => {
    setLoading(true);
    if (!question || !answer) {
      swalWarningMessage("Please fill all the fields");
      setLoading(false);
      return;
    }
    const obj = {
      question: question,
      answer: answer,
      project_id: projectId,
    };
    const res: any = await dispatch(addRagQuestionAction(obj));
    await fetchRagData();
    setState((prev) => ({
      ...prev,
      addModal: false,
      question: "",
      answer: "",
    }));
    setPopupState({
      open: true,
      message: "Question and Answer Added successfully.",
      heading: "Added",
      Icon: SuccessIcon,
    });
    setLoading(false);
  };

  const applyRefresh = async () => {
    setRefreshLoading(true);
    await fetchRagData();
    setRefreshLoading(false);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (mainLoading) {
    return <PageLoader height="90vh" />;
  }

  return (
    <Box>
      <Box className="flex-between">
        <Typography className="mb-2 font-20-grey">Bot's Knowledge</Typography>

        <Box className="flex-align-center" gap={1}>
          <SearchBar
            searchText={searchText}
            handleChange={(e) => {
              e.persist();
              setState((prev) => ({
                ...prev,
                searchText: e.target.value,
              }));
            }}
          />
          <IconButton
            title="Refresh"
            onClick={applyRefresh}
            sx={{
              ...commonStyles.refreshButtonStyle,
              ...dashboardStyles.refreshAnimation(refreshLoading),
            }}
            disabled={loading}
          >
            <RefreshIcon className="refresh-icon" height={14} width={14} />
          </IconButton>
          <BaseButton
            title="Upload File"
            variant="outlined"
            handleClick={() =>
              setState((prev) => ({
                ...prev,
                showUploadModal: true,
              }))
            }
          />
          <BaseButton
            title="Add New Question"
            variant="contained"
            handleClick={() =>
              setState((prev) => ({
                ...prev,
                addModal: true,
              }))
            }
            startIcon={<AddIcon />}
          />
        </Box>
      </Box>

      <Box className="mt-4">
        <DynamicTable
          columns={[
            {
              label: "QUESTION",
              render: (row) => {
                return (
                  <Box sx={commonStyles.tableRowText}>
                    {shortenString(row?.question, 140)}
                  </Box>
                );
              },
            },
            {
              label: "ANSWER",
              render: (row) => {
                return (
                  <Box sx={commonStyles.tableRowText}>
                    {shortenString(row?.answer, 140)}
                  </Box>
                );
              },
            },
            {
              label: "ACTIONS",
              render: (row) => (
                <>
                  <Box>
                    <IconButton
                      onClick={() => editToggle(row, true)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => editToggle(row, false)}
                      title="View"
                    >
                      <VisibilityOutlinedIcon
                        fontSize="small"
                        sx={{ transform: "scale(0.85)" }}
                      />
                    </IconButton>
                    <IconButton
                      onClick={() =>
                        setState((prev) => ({
                          ...prev,
                          confirmationPopUp: true,
                          ragId: row?._id,
                        }))
                      }
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </>
              ),
            },
          ]}
          data={ragData}
          loading={false}
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          noDataAvailableMessage={"No Question Available"}
          totalRowCount={totalRagCount}
          rowCount={ragData?.length}
        />
      </Box>

      {showUploadModal && (
        <CommonModal
          body={<UploadBody setFiles={setFiles} />}
          handleSave={() => onUploadClick(false)}
          heading={`Upload Document`}
          show={showUploadModal}
          size={600}
          toggle={() => {
            setState((prev) => ({
              ...prev,
              showUploadModal: false,
            }));
            setFiles([]);
          }}
          saveButtonText="Upload"
          spin={loading}
        />
      )}
      {addModal && (
        <CommonModal
          body={<AddRagBody setState={setState} state={state} />}
          handleSave={addQuestionAns}
          heading={`Add Question`}
          show={addModal}
          size={600}
          toggle={() => {
            setState((prev) => ({
              ...prev,
              addModal: false,
              question: "",
              answer: "",
              ragId: "",
            }));
          }}
          saveButtonText="Add"
          spin={loading}
        />
      )}
      {showEditModal && (
        <CommonModal
          body={
            <RagEditBody
              setState={setState}
              state={state}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
            />
          }
          handleSave={updateRagData}
          heading={isEdit ? "Edit Question" : "View Question"}
          show={showEditModal}
          size={600}
          toggle={() =>
            setState((prev) => ({
              ...prev,
              showEditModal: false,
              answer: "",
              question: "",
              ragId: "",
            }))
          }
          showSaveButton={isEdit ? true : false}
          saveButtonText="Save"
          spin={loading}
        />
      )}

      {confirmationPopUp && (
        <DeletePopup
          message="Are you sure want to delete the selected question ?"
          show={confirmationPopUp}
          toggle={() =>
            setState((prev) => ({ ...prev, confirmationPopUp: false }))
          }
          handleDelete={handleDelete}
          spin={loading}
          heading="Delete Question"
        />
      )}

      {open && (
        <CommonPopup
          message={message}
          show={open}
          toggle={() =>
            setPopupState((prev) => ({
              ...prev,
              open: false,
            }))
          }
          Icon={Icon}
          heading={heading}
        />
      )}
      {reuploadConfirmationPopup && (
        <ConfirmationPopup
          show={reuploadConfirmationPopup}
          toggle={() => setReuploadConfirmationPopup(false)}
          handleSave={() => onUploadClick(true)}
          heading={"Warning"}
          message={
            "Uploading new files will replace your existing data. Do you want to continue?"
          }
          spin={loading}
          Icon={ConfirmationIcon}
        />
      )}
    </Box>
  );
};

export default RagUpload;

const UploadBody = ({ setFiles }) => {
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;

    if (selectedFiles) {
      const filesArray = Array.from(selectedFiles);
      setLocalFiles((prev) => [...prev, ...filesArray]);
      setFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    setLocalFiles(droppedFiles);
    setFiles(droppedFiles);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box>
      <Box
        sx={commonStyles.uploadContainer}
        onClick={triggerFileInput}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          accept=".pdf,.txt,.docx,.xlsx"
          multiple
        />

        {localFiles.length > 0 ? (
          <Box>
            {localFiles.map((f, idx) => (
              <Typography key={idx}>{f.name}</Typography>
            ))}
          </Box>
        ) : (
          <Typography>Drag and drop files here, or click to select</Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            triggerFileInput();
          }}
          sx={{ mt: 2 }}
        >
          Browse Files
        </Button>
      </Box>
    </Box>
  );
};
